import { useMemo, useState } from 'react';

import { toast } from 'react-toastify';

import {
  type CustomerTier,
  useCreateCustomerTier,
  useDeleteCustomerTier,
  useEnableCustomerTierForAllLocations,
  useListCustomerTiers,
  useSetCustomerLocationTierAccess,
  useUpdateCustomerTier,
} from '../../features/customer-tiers/api';

import { useListAddresses, useListCustomers } from '../../features/customers/api';

import './customer-tiers.scss';

const DEFAULT_BADGE_COLOR = '#64748B';
const HEX_COLOR_PATTERN = /^#[0-9A-Fa-f]{6}$/;

function errorMessage(error: any, fallback: string) {
  return error?.response?.data?.error || fallback;
}

function normalizeBadgeColor(color: string) {
  return HEX_COLOR_PATTERN.test(color) ? color : DEFAULT_BADGE_COLOR;
}

function dollarsToCents(value: string) {
  const amount = Number(value);

  if (!Number.isFinite(amount) || amount < 0) {
    return null;
  }

  return Math.round((amount + Number.EPSILON) * 100);
}

function centsToDollarsInput(cents: number) {
  return (Number(cents || 0) / 100).toFixed(2);
}

function formatDiscountAmount(cents: number) {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
  }).format(Number(cents || 0) / 100);
}

function usesDarkText(hexColor: string) {
  const normalized = normalizeBadgeColor(hexColor).replace('#', '');

  const r = parseInt(normalized.slice(0, 2), 16);

  const g = parseInt(normalized.slice(2, 4), 16);

  const b = parseInt(normalized.slice(4, 6), 16);

  const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;

  return luminance > 0.62;
}

function TierBadge({ label, color }: { label: string; color: string }) {
  const normalizedColor = normalizeBadgeColor(color);

  return (
    <span
      className={`customer-tier-badge ${
        usesDarkText(normalizedColor)
          ? 'customer-tier-badge--dark-text'
          : 'customer-tier-badge--light-text'
      }`}
    >
      <svg
        className="customer-tier-badge__background"
        viewBox="0 0 100 32"
        preserveAspectRatio="none"
        aria-hidden="true"
        focusable="false"
      >
        <rect width="100" height="32" rx="16" fill={normalizedColor} />
      </svg>

      <span className="customer-tier-badge__label">{label}</span>
    </span>
  );
}

function TierColorSwatch({ color }: { color: string }) {
  const normalizedColor = normalizeBadgeColor(color);

  return (
    <svg
      className="customer-tier-color-swatch"
      viewBox="0 0 20 20"
      aria-hidden="true"
      focusable="false"
    >
      <rect x="0.5" y="0.5" width="19" height="19" rx="4" fill={normalizedColor} />
    </svg>
  );
}

export default function CustomerTiersPage() {
  const { data, isLoading } = useListCustomerTiers();

  const createTier = useCreateCustomerTier();

  const updateTier = useUpdateCustomerTier();

  const deleteTier = useDeleteCustomerTier();

  const setLocationTierAccess = useSetCustomerLocationTierAccess();

  const enableTierForAllLocations = useEnableCustomerTierForAllLocations();

  const tiers = useMemo(() => data?.tiers ?? [], [data?.tiers]);

  const [managingTierId, setManagingTierId] = useState<string | null>(null);

  const [customerSearch, setCustomerSearch] = useState('');

  const [assignmentCustomerId, setAssignmentCustomerId] = useState('');

  const [selectAllLocations, setSelectAllLocations] = useState(false);

  const customersQuery = useListCustomers({
    page: 1,
    pageSize: 100,
    search: customerSearch.trim(),
    status: '',
  });

  const assignmentCustomers = customersQuery.data?.customers ?? [];

  const { data: assignmentAddressData, isLoading: assignmentAddressesLoading } = useListAddresses(
    assignmentCustomerId || null
  );

  const assignmentAddresses = assignmentAddressData?.addresses ?? [];

  const assignmentCustomerTier = assignmentAddressData?.customerTier ?? null;

  const managingTier = managingTierId
    ? (tiers.find((tier) => tier.id === managingTierId) ?? null)
    : null;

  const [editingTierId, setEditingTierId] = useState<string | null>(null);

  const [name, setName] = useState('');

  const [discountAmount, setDiscountAmount] = useState('');

  const [badgeColor, setBadgeColor] = useState(DEFAULT_BADGE_COLOR);

  const isSaving = createTier.isPending || updateTier.isPending;

  const isUpdatingLocationAccess =
    setLocationTierAccess.isPending || enableTierForAllLocations.isPending;

  const editingTier = useMemo(
    () => (editingTierId ? (tiers.find((tier) => tier.id === editingTierId) ?? null) : null),
    [editingTierId, tiers]
  );

  const allLocationsEnabledForManagingTier = Boolean(
    managingTier &&
    assignmentCustomerTier?.id === managingTier.id &&
    assignmentAddresses.length > 0 &&
    assignmentAddresses.every((address: any) => Boolean(address.applyCustomerTierDiscount))
  );

  const resetForm = () => {
    setEditingTierId(null);
    setName('');
    setDiscountAmount('');
    setBadgeColor(DEFAULT_BADGE_COLOR);
  };

  const resetLocationManager = () => {
    setManagingTierId(null);
    setAssignmentCustomerId('');
    setCustomerSearch('');
    setSelectAllLocations(false);
  };

  const beginEdit = (tier: CustomerTier) => {
    setEditingTierId(tier.id);
    setName(tier.name);

    setDiscountAmount(centsToDollarsInput(tier.discountAmountCents));

    setBadgeColor(tier.badgeColor || DEFAULT_BADGE_COLOR);
  };

  const onSave = async () => {
    const trimmedName = name.trim();

    const discountAmountCents = dollarsToCents(discountAmount);

    if (!trimmedName) {
      toast.warn('Tier name is required.');

      return;
    }

    if (discountAmountCents === null) {
      toast.warn('Discount must be a valid non-negative dollar amount.');

      return;
    }

    if (!HEX_COLOR_PATTERN.test(badgeColor)) {
      toast.warn('Choose a valid badge color.');

      return;
    }

    try {
      if (editingTierId) {
        await updateTier.mutateAsync({
          tierId: editingTierId,
          name: trimmedName,
          discountAmountCents,
          badgeColor: badgeColor.toUpperCase(),
        });

        toast.success('Customer tier updated.');
      } else {
        const created = await createTier.mutateAsync({
          name: trimmedName,
          discountAmountCents,
          badgeColor: badgeColor.toUpperCase(),
        });

        toast.success('Customer tier created.');

        if (created?.tier?.id) {
          setManagingTierId(created.tier.id);

          setSelectAllLocations(false);
        }
      }

      resetForm();
    } catch (error) {
      toast.error(
        errorMessage(error, editingTierId ? 'Failed to update tier.' : 'Failed to create tier.')
      );
    }
  };

  const onDelete = async (tier: CustomerTier) => {
    const assignmentMessage =
      tier.customerCount > 0
        ? `\n\nCurrently assigned to ${tier.customerCount} customer${
            tier.customerCount === 1 ? '' : 's'
          }. Those customer tier assignments will be cleared and their location discounts will be disabled.`
        : '';

    const confirmed = window.confirm(`Delete "${tier.name}"?${assignmentMessage}`);

    if (!confirmed) {
      return;
    }

    try {
      const result = await deleteTier.mutateAsync(tier.id);

      if (editingTierId === tier.id) {
        resetForm();
      }

      if (managingTierId === tier.id) {
        resetLocationManager();
      }

      const disabledLocationCount = result?.disabledLocationCount ?? 0;

      if (disabledLocationCount > 0) {
        toast.success(
          `Customer tier deleted. Disabled the tier discount at ${disabledLocationCount} location${
            disabledLocationCount === 1 ? '' : 's'
          }.`
        );
      } else {
        toast.success('Customer tier deleted.');
      }
    } catch (error) {
      toast.error(errorMessage(error, 'Failed to delete tier.'));
    }
  };

  const onEnableAllLocations = async () => {
    if (!assignmentCustomerId || !managingTier || !selectAllLocations) {
      return;
    }

    try {
      const result = await enableTierForAllLocations.mutateAsync({
        customerId: assignmentCustomerId,

        tierId: managingTier.id,
      });

      setSelectAllLocations(false);

      const updatedCount = result?.tierLocationsUpdated ?? assignmentAddresses.length;

      toast.success(
        `Assigned ${managingTier.name} to the customer and enabled the tier discount at ${updatedCount} location${
          updatedCount === 1 ? '' : 's'
        }.`
      );
    } catch (error) {
      toast.error(errorMessage(error, 'Failed to enable the tier discount for all locations.'));
    }
  };

  const previewColor = normalizeBadgeColor(badgeColor);

  return (
    <div className="card customer-tiers-page">
      <div className="flex-between customer-tiers-page__header">
        <div>
          <h2 className="card__title">Customer Tiers</h2>

          <p className="card__subtitle">
            Configure fixed-dollar customer pricing tiers and choose which customer locations
            receive the customer&apos;s tier discount.
          </p>
        </div>
      </div>

      <div className="card customer-tiers-editor">
        <h3 className="customer-tiers-editor__title">
          {editingTier ? `Edit ${editingTier.name}` : 'Add Customer Tier'}
        </h3>

        <div className="grid customer-tiers-editor__grid">
          <div className="form-control">
            <label>Tier Name *</label>

            <input
              value={name}
              maxLength={100}
              placeholder="Gold"
              onChange={(event) => setName(event.target.value)}
            />
          </div>

          <div className="form-control">
            <label>Discount Amount ($)</label>

            <input
              type="number"
              min="0"
              step="0.01"
              value={discountAmount}
              placeholder="10.00"
              onChange={(event) => setDiscountAmount(event.target.value)}
            />
          </div>

          <div className="form-control">
            <label>Badge Color</label>

            <div className="customer-tiers-color-control">
              <input
                className="customer-tiers-color-control__picker"
                type="color"
                value={previewColor}
                onChange={(event) => setBadgeColor(event.target.value)}
                aria-label="Tier badge color"
              />

              <input
                className="customer-tiers-color-control__hex"
                value={badgeColor}
                maxLength={7}
                onChange={(event) => setBadgeColor(event.target.value)}
                aria-label="Tier badge color hex value"
              />
            </div>
          </div>
        </div>

        <div className="customer-tiers-preview">
          <TierBadge label={name.trim() || 'Tier Preview'} color={previewColor} />

          <span className="muted tiny">Badge preview</span>
        </div>

        <div className="customer-tiers-editor__actions">
          <button type="button" className="btn btn--primary" onClick={onSave} disabled={isSaving}>
            {isSaving ? 'Saving…' : editingTierId ? 'Save Changes' : 'Add Tier'}
          </button>

          {editingTierId && (
            <button
              type="button"
              className="btn btn--ghost"
              onClick={resetForm}
              disabled={isSaving}
            >
              Cancel
            </button>
          )}
        </div>
      </div>

      {isLoading ? (
        <div className="skeleton customer-tiers-page__skeleton" />
      ) : tiers.length === 0 ? (
        <div className="muted customer-tiers-page__empty">No customer tiers configured yet.</div>
      ) : (
        <div className="customer-tiers-table-wrap">
          <table className="table customer-tiers-table">
            <thead>
              <tr>
                <th>Tier</th>
                <th>Discount</th>
                <th>Customers</th>
                <th>Color</th>

                <th className="ta-right">Actions</th>
              </tr>
            </thead>

            <tbody>
              {tiers.map((tier) => (
                <tr key={tier.id}>
                  <td>
                    <TierBadge label={tier.name} color={tier.badgeColor} />
                  </td>

                  <td>{formatDiscountAmount(tier.discountAmountCents)}</td>

                  <td>{tier.customerCount}</td>

                  <td>
                    <div className="customer-tier-color-value">
                      <TierColorSwatch color={tier.badgeColor} />

                      <span className="muted tiny">{tier.badgeColor}</span>
                    </div>
                  </td>

                  <td className="ta-right">
                    <div className="customer-tiers-table__actions">
                      <button
                        type="button"
                        className="btn btn--ghost"
                        onClick={() => {
                          setManagingTierId(tier.id);

                          setAssignmentCustomerId('');

                          setCustomerSearch('');

                          setSelectAllLocations(false);
                        }}
                      >
                        Locations
                      </button>

                      <button
                        type="button"
                        className="btn btn--ghost"
                        onClick={() => beginEdit(tier)}
                      >
                        Edit
                      </button>

                      <button
                        type="button"
                        className="btn btn--ghost"
                        disabled={deleteTier.isPending}
                        onClick={() => onDelete(tier)}
                      >
                        Delete
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {managingTier && (
        <div className="card customer-tiers-location-manager">
          <div
            className="flex-between"
            style={{
              gap: 12,
              marginBottom: 16,
            }}
          >
            <div>
              <h3
                style={{
                  margin: 0,
                }}
              >
                Manage {managingTier.name} Location Access
              </h3>

              <div
                className="muted tiny"
                style={{
                  marginTop: 4,
                }}
              >
                {formatDiscountAmount(managingTier.discountAmountCents)} discount. The tier is
                assigned to the customer; each location only controls whether the customer&apos;s
                tier discount is enabled there.
              </div>
            </div>

            <button type="button" className="btn btn--ghost" onClick={resetLocationManager}>
              Close
            </button>
          </div>

          <div
            className="grid"
            style={{
              gridTemplateColumns: '1fr 1fr',
              gap: 12,
            }}
          >
            <div className="form-control">
              <label>Search Customer</label>

              <input
                value={customerSearch}
                placeholder="Name or email…"
                onChange={(event) => {
                  setCustomerSearch(event.target.value);

                  setAssignmentCustomerId('');

                  setSelectAllLocations(false);
                }}
              />
            </div>

            <div className="form-control">
              <label>Customer</label>

              <select
                value={assignmentCustomerId}
                onChange={(event) => {
                  setAssignmentCustomerId(event.target.value);

                  setSelectAllLocations(false);
                }}
              >
                <option value="">
                  {customersQuery.isLoading ? 'Loading customers…' : 'Select customer…'}
                </option>

                {assignmentCustomers.map((customer: any) => (
                  <option key={customer.id} value={customer.id}>
                    {customer.name || customer.email}

                    {customer.email && customer.name ? ` · ${customer.email}` : ''}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {assignmentCustomerId && (
            <div
              style={{
                marginTop: 16,
              }}
            >
              {assignmentAddressesLoading ? (
                <div
                  className="skeleton"
                  style={{
                    height: 120,
                  }}
                />
              ) : (
                <>
                  <div
                    className="card"
                    style={{
                      padding: 12,
                      marginBottom: 12,
                    }}
                  >
                    <div
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        flexWrap: 'wrap',
                        gap: 8,
                      }}
                    >
                      <strong>Customer tier:</strong>

                      {assignmentCustomerTier ? (
                        <>
                          <TierBadge
                            label={assignmentCustomerTier.name}
                            color={assignmentCustomerTier.badgeColor}
                          />

                          <span className="muted tiny">
                            {formatDiscountAmount(assignmentCustomerTier.discountAmountCents)}{' '}
                            discount
                          </span>
                        </>
                      ) : (
                        <span className="muted">No tier assigned</span>
                      )}
                    </div>
                  </div>

                  {assignmentCustomerTier && assignmentCustomerTier.id !== managingTier.id && (
                    <div
                      className="banner banner--warn"
                      style={{
                        marginBottom: 12,
                      }}
                    >
                      Enabling {managingTier.name} will replace this customer&apos;s current{' '}
                      {assignmentCustomerTier.name} tier. Every location already enabled for tier
                      discounts will then use {managingTier.name}.
                    </div>
                  )}

                  {assignmentAddresses.length > 0 && (
                    <div
                      className="card"
                      style={{
                        padding: 12,
                        marginBottom: 12,
                      }}
                    >
                      <div
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          flexWrap: 'wrap',
                          gap: 12,
                        }}
                      >
                        <label
                          style={{
                            display: 'flex',
                            alignItems: 'flex-start',
                            gap: 10,
                            cursor: allLocationsEnabledForManagingTier ? 'default' : 'pointer',
                            margin: 0,
                          }}
                        >
                          <input
                            type="checkbox"
                            checked={allLocationsEnabledForManagingTier || selectAllLocations}
                            disabled={
                              allLocationsEnabledForManagingTier || isUpdatingLocationAccess
                            }
                            onChange={(event) => setSelectAllLocations(event.target.checked)}
                            style={{
                              width: 16,
                              height: 16,
                              marginTop: 2,
                            }}
                          />

                          <span>
                            <strong>Select all locations</strong>

                            <span
                              className="muted tiny"
                              style={{
                                display: 'block',
                                marginTop: 2,
                              }}
                            >
                              Assign {managingTier.name} to this customer and enable its discount at
                              all {assignmentAddresses.length} delivery location
                              {assignmentAddresses.length === 1 ? '' : 's'}.
                            </span>
                          </span>
                        </label>

                        <button
                          type="button"
                          className="btn btn--primary"
                          disabled={
                            !selectAllLocations ||
                            allLocationsEnabledForManagingTier ||
                            isUpdatingLocationAccess
                          }
                          onClick={onEnableAllLocations}
                        >
                          {enableTierForAllLocations.isPending
                            ? 'Enabling…'
                            : allLocationsEnabledForManagingTier
                              ? 'All locations enabled'
                              : 'Enable all locations'}
                        </button>
                      </div>
                    </div>
                  )}

                  {assignmentAddresses.length === 0 ? (
                    <div className="muted">This customer has no delivery locations.</div>
                  ) : (
                    <div className="customer-tiers-table-wrap">
                      <table className="table customer-tiers-table">
                        <thead>
                          <tr>
                            <th>Location</th>

                            <th>Address</th>

                            <th>Tier Discount</th>

                            <th className="ta-right">Action</th>
                          </tr>
                        </thead>

                        <tbody>
                          {assignmentAddresses.map((address: any) => {
                            const customerUsesManagingTier =
                              assignmentCustomerTier?.id === managingTier.id;

                            const enabledForManagingTier =
                              customerUsesManagingTier &&
                              Boolean(address.applyCustomerTierDiscount);

                            const replacingTier = Boolean(
                              assignmentCustomerTier &&
                              assignmentCustomerTier.id !== managingTier.id
                            );

                            const currentDiscountEnabled =
                              Boolean(assignmentCustomerTier) &&
                              Boolean(address.applyCustomerTierDiscount);

                            const nextEnabled = !enabledForManagingTier;

                            return (
                              <tr key={address.id}>
                                <td>
                                  <strong>{address.label || address.fullName || 'Location'}</strong>
                                </td>

                                <td className="muted">
                                  {[address.line1, address.city, address.state, address.postalCode]
                                    .filter(Boolean)
                                    .join(', ')}
                                </td>

                                <td>
                                  {currentDiscountEnabled ? (
                                    <span>Enabled · {assignmentCustomerTier?.name}</span>
                                  ) : (
                                    <span className="muted">Disabled</span>
                                  )}
                                </td>

                                <td className="ta-right">
                                  <button
                                    type="button"
                                    className="btn btn--ghost"
                                    disabled={isUpdatingLocationAccess}
                                    onClick={async () => {
                                      try {
                                        await setLocationTierAccess.mutateAsync({
                                          customerId: assignmentCustomerId,

                                          addressId: address.id,

                                          tierId: managingTier.id,

                                          enabled: nextEnabled,
                                        });

                                        if (!nextEnabled) {
                                          toast.success('Tier discount disabled for location.');
                                        } else if (replacingTier) {
                                          toast.success(
                                            `Customer tier changed to ${managingTier.name} and the location discount was enabled.`
                                          );
                                        } else if (!assignmentCustomerTier) {
                                          toast.success(
                                            `Assigned ${managingTier.name} to the customer and enabled the location discount.`
                                          );
                                        } else {
                                          toast.success('Tier discount enabled for location.');
                                        }
                                      } catch (error) {
                                        toast.error(
                                          errorMessage(
                                            error,
                                            'Failed to update location tier access.'
                                          )
                                        );
                                      }
                                    }}
                                  >
                                    {setLocationTierAccess.isPending
                                      ? 'Saving…'
                                      : enabledForManagingTier
                                        ? 'Disable'
                                        : replacingTier
                                          ? `Use ${managingTier.name}`
                                          : assignmentCustomerTier
                                            ? 'Enable'
                                            : 'Assign & enable'}
                                  </button>
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  )}
                </>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
